import axios from 'axios';
import { clearLocalStorage } from 'legacy-ft-cb';
import { LC_CONSTANTS } from 'legacy-src/components/impersonating/utils/constants';
import log from 'chameleon/ui-stack/utilities/log';
import LOGOUT_WHITELIST from 'chameleon/ui-stack/constants/lc-logout-whitelist';

export const LOCKDOWN_PAGES = [
  'lockdown', // Used for downloading Lockdown Browser
  'cf_dl', // Used for downloading Lockdown Browser (S3 Download)
  'securebrowsercheck', // Used for checking Lockdown Browser
  'close', // Used for closing Lockdown Browser
  'readinesscheck', // Used to show Lockdown's Test Exam (to verify everything works)
];

export const STANDALONE_PAGES = [
  ...LOCKDOWN_PAGES,
  'ps-answer-sheets',
  'login',
  'version',
  'logout',
  'join',
];

document.addEventListener(
  'visibilitychange',
  () => {
    const splitPathname = window.location.pathname.split('/');
    const shouldRedirect = !STANDALONE_PAGES.includes(splitPathname[1]); // 0 index is empty as `pathname` begins with a `/`

    if (!document.hidden && shouldRedirect) {
      axios({
        method: 'get',
        url: `${FYM_ACCOUNTS_URL}/auth/verify/`,
      });
    }
  },
  false,
);

/**
 * Performs redirect to login upon errors.
 * @param {string} redirectPath Allows for specifying redirect path.
 * @param {object} router - optional
 */
export function redirectToLogin(redirectPath = '', router) {
  /**
   * Set the redirect_path so long as path is not version, login, logout, join, auth/redirect or root.
   */
  let path = `${FYM_FRONTEND_URL}/login`;

  const split = redirectPath.split('/');

  const redirect =
    !/(version$)|(login$)|(close$)|(lockdown$)|(cf_dl$)|(securebrowsercheck$)|(logout$)|(join$)|(auth\/redirect$)|(^\/$)/i.test(
      split[1],
    );
  // ^ 0 index is empty as `pathname` begins with a `/`

  if (redirectPath && redirect) {
    path += `?redirect_path=${redirectPath}`;
  }

  if (router) {
    router.replace(redirectPath || '/login');
  } else {
    window.location.href = path;
  }
}

export const getSubjectEducationPeriods = ({ subjectId, teacherSubjects }) => {
  if (!teacherSubjects) {
    return [];
  }

  const teacherSubject = teacherSubjects.find(
    (s) => parseInt(s.id, 10) === parseInt(subjectId, 10),
  );
  return (
    (teacherSubject &&
      teacherSubject.educationPeriods &&
      teacherSubject.educationPeriods.length &&
      teacherSubject.educationPeriods) ||
    []
  );
};

export const getEducationPeriodForSubject = ({ subjectId, userData }) => {
  if (!userData) {
    return '';
  }

  const {
    teacherSubjects,
    educationPeriod: { currentEducationPeriod, nextEducationPeriod },
    chosenEducationPeriodCode,
  } = userData;

  const subjectEducationPeriods = getSubjectEducationPeriods({
    subjectId,
    teacherSubjects,
  });

  const subjectHasCurrentEducationPeriod =
    !!currentEducationPeriod &&
    !!subjectEducationPeriods.find(
      (subjectEducationPeriod) =>
        subjectEducationPeriod.id === currentEducationPeriod.id,
    );
  const subjectHasNextEducationPeriod =
    !!nextEducationPeriod &&
    !!subjectEducationPeriods.find(
      (subjectEducationPeriod) =>
        subjectEducationPeriod.id === nextEducationPeriod.id,
    );

  const hasAccessToAnyPeriod = !!(
    subjectEducationPeriods && subjectEducationPeriods.length
  );
  const latestPeriodWithAccess =
    hasAccessToAnyPeriod &&
    subjectEducationPeriods
      .slice()
      .sort((a, b) => new Date(b.endDate) - new Date(a.endDate));

  const educationPeriod =
    chosenEducationPeriodCode ||
    (subjectHasCurrentEducationPeriod && currentEducationPeriod.id) ||
    (subjectHasNextEducationPeriod && nextEducationPeriod.id) ||
    (hasAccessToAnyPeriod && latestPeriodWithAccess[0].id);

  return educationPeriod || '';
};

export const accessTokenRefresh = (props) => {
  const { refreshTokenKey, accessTokenKey, expiresKey } = props || {
    refreshTokenKey: LC_CONSTANTS.RT,
    accessTokenKey: LC_CONSTANTS.AT,
    expiresKey: LC_CONSTANTS.EXP,
  };

  const accessToken = window.localStorage.getItem(accessTokenKey);
  const refreshToken = window.localStorage.getItem(refreshTokenKey);
  // make a new request here so that we can set headers
  // you have to set headers before you send the actual request - otherwise they wont show
  const getRefresh = axios.create({
    baseURL: `${FYM_ACCOUNTS_URL}/token/refresh/`,
    data: { access_token: accessToken },
  });
  //set headers
  getRefresh.defaults.headers.common.Authorization = `Bearer ${refreshToken}`;
  const prom = getRefresh
    .post()
    .then((resp) => {
      if (resp.data.access_token) {
        //got new account_access_token - set it to storage
        window.localStorage.setItem(accessTokenKey, resp.data.access_token);
        window.localStorage.setItem(expiresKey, Date.parse(resp.data.expires));
        return resp;
      }
      throw new Error('Error processing the request. Please login again.');
    })
    .catch((err) => {
      console.error('Refresh error', err); // eslint-disable-line no-console
      clearLocalStorage(LOGOUT_WHITELIST);
      log('error', err);
      redirectToLogin();
      throw err;
    })
    .finally(accessTokenRefresh.cancel);
  return prom;
};

/**
 * Given url, should we redirect to SSO?
 * The return value is a boolean, in all cases.
 * @param {string} url - The url, as a string, on which we decide
 */
export const shouldRedirectToSSO = (url) => {
  // Unless the first level pages are login, logout - send them to SSO
  // No:
  // apclassroom.collegeboard.org/login
  // apclassroom.collegeboard.org/logout
  // apclassroom.collegeboard.org/

  // SSO if pattern like "/78/assessments/assignments/173784"
  // this should not change behavior of apclassroom.collegeboard.org/login?directlogin=blah
  const logPageRegExp = RegExp('^$|^/$|log(in|out)', 'i');
  const urlParsed = new URL(url);

  if (logPageRegExp.test(urlParsed.pathname)) {
    return false;
  }

  return true;
};
